import { z } from "zod";

export const FLASHCARD_LIMIT = 5_000;
export const FLASHCARD_IMPORT_BYTES = 5 * 1024 * 1024;
export const flashcardTextSchema = z.object({
  front: z.string().trim().min(1).max(4_000),
  back: z.string().trim().min(1).max(4_000),
}).strict();
export const flashcardDeckCreateSchema = z.object({
  title: z.string().trim().min(1).max(160),
  description: z.string().trim().max(1_000).default(""),
  organizationId: z.uuid().optional(),
}).strict();
export const flashcardDeckUpdateSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  description: z.string().trim().max(1_000).optional(),
  archived: z.boolean().optional(),
  revision: z.number().int().positive(),
}).strict();
export const flashcardUpdateSchema = flashcardTextSchema.partial().extend({
  revision: z.number().int().positive(),
  archived: z.boolean().optional(),
  reset: z.boolean().optional(),
}).strict();
export const flashcardCopySchema = z.object({ learnerIds: z.array(z.uuid()).max(100).default([]) }).strict();
export const flashcardVisibilitySchema = z.object({ shared: z.boolean() }).strict();
export const flashcardImportSchema = z.object({
  text: z.string().max(FLASHCARD_IMPORT_BYTES),
  delimiter: z.enum([",", ";", "\t"]),
  header: z.boolean(),
  frontColumn: z.union([z.literal(0), z.literal(1)]),
  commit: z.boolean().default(false),
}).strict();
export const flashcardReviewSchema = z.object({
  presentationId: z.uuid(),
  rating: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
}).strict();
export type FlashcardText = z.infer<typeof flashcardTextSchema>;
export type FlashcardRating = z.infer<typeof flashcardReviewSchema>["rating"];

/** Two-column CSV/TSV, including quoted delimiters and embedded newlines. */
export function parseFlashcardImport(input: z.infer<typeof flashcardImportSchema>, existing: readonly FlashcardText[] = []) {
  const errors: Array<{ line: number; message: string }> = [];
  if (new TextEncoder().encode(input.text).length > FLASHCARD_IMPORT_BYTES) {
    return { cards: [] as FlashcardText[], duplicates: 0, errors: [{ line: 1, message: "Die Datei darf höchstens 5 MiB groß sein." }] };
  }
  const text = input.text.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  const rows: Array<{ line: number; fields: string[] }> = [];
  let fields: string[] = [], field = "", quoted = false, closed = false, line = 1, start = 1;
  for (let i = 0; i <= text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === undefined) { errors.push({ line: start, message: "Ein Anführungszeichen wurde nicht geschlossen." }); break; }
      if (char === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { quoted = false; closed = true; }
      } else { field += char; if (char === "\n") line++; }
    } else if (char === input.delimiter || char === "\n" || char === undefined) {
      fields.push(field); field = ""; closed = false;
      if (char !== input.delimiter) {
        if (fields.some((value) => value.trim())) rows.push({ line: start, fields });
        fields = []; if (char === "\n") line++; start = line;
      }
    } else if (char === '"' && field === "" && !closed) quoted = true;
    else if (closed || char === '"') {
      errors.push({ line, message: "Ungültige Zeichen außerhalb eines zitierten Feldes." }); break;
    } else field += char;
  }
  const key = (card: FlashcardText) => JSON.stringify([card.front.normalize("NFC").trim(), card.back.normalize("NFC").trim()]);
  const known = new Set(existing.map(key));
  const cards: FlashcardText[] = [];
  let duplicates = 0;
  for (const row of rows.slice(input.header ? 1 : 0)) {
    const parsed = flashcardTextSchema.safeParse({ front: row.fields[input.frontColumn], back: row.fields[1 - input.frontColumn] });
    if (row.fields.length !== 2 || !parsed.success) {
      errors.push({ line: row.line, message: "Erwartet werden zwei nicht leere Spalten mit höchstens 4.000 Zeichen je Seite." }); continue;
    }
    if (known.has(key(parsed.data))) duplicates++;
    else { known.add(key(parsed.data)); cards.push(parsed.data); }
  }
  if (cards.length + existing.length > FLASHCARD_LIMIT) errors.push({ line: 1, message: "Ein Stapel darf höchstens 5.000 Karten enthalten." });
  return { cards, duplicates, errors };
}
